import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { NotificationsNone as NotificationsNoneIcon } from '@mui/icons-material';
import { API_URL } from '../config/api';
import { tokenManager } from '../context/AuthContext';

const POLL_MS = 2 * 60 * 1000;

const call = async (method, path) => {
  const res = await fetch(`${API_URL}/notifications${path}`, {
    method,
    headers: { ...tokenManager.getAuthHeader(), 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
};

const timeAgo = (value) => {
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
};

// Personal notifications of the signed-in user (e.g. Expense approvals). Separate from the
// HR admin inbox (NotificationSystem), which only shows notifications without a recipient.
const MyNotificationsBell = ({ color = 'inherit' }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!tokenManager.isAuthenticated()) return;
    try {
      const res = await call('GET', '/mine?limit=20');
      setItems(res.data || []);
      setUnread(res.unread || 0);
    } catch (e) {
      // Non-critical UI: keep the last known state
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const open = (event) => {
    setAnchor(event.currentTarget);
    load();
  };

  const select = async (n) => {
    setAnchor(null);
    if (!n.isRead) {
      try {
        await call('PATCH', `/mine/${n._id}/read`);
      } catch (e) {
        // ignore
      }
      load();
    }
    if (n.link) navigate(pathname.startsWith('/admin/') ? `/admin${n.link}` : n.link);
  };

  const readAll = async () => {
    try {
      await call('PATCH', '/mine/read-all');
    } catch (e) {
      // ignore
    }
    load();
  };

  return (
    <>
      <Tooltip title="My notifications">
        <IconButton onClick={open} sx={{ color }} aria-label="My notifications">
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 360, maxHeight: 440, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>Notifications</Typography>
            {unread > 0 && <Button size="small" onClick={readAll} sx={{ textTransform: 'none' }}>Mark all read</Button>}
          </Box>
          <Divider />
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No notifications</Typography>
          ) : (
            <List dense sx={{ overflowY: 'auto' }}>
              {items.map((n) => (
                <ListItemButton key={n._id} onClick={() => select(n)} sx={{ bgcolor: n.isRead ? 'transparent' : 'action.hover', alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ fontWeight: n.isRead ? 400 : 600 }}>{n.title}</Typography>}
                    secondary={(
                      <>
                        <Typography component="span" variant="caption" sx={{ display: 'block' }}>{n.message}</Typography>
                        <Typography component="span" variant="caption" color="text.secondary">{timeAgo(n.createdAt)}</Typography>
                      </>
                    )}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MyNotificationsBell;
